const CartOptimistic = {
  LEAVING_CLASS: 'cart-line-leaving',
  UNRESOLVED_CLASS: 'cart-line-unresolved',
  // Resolved with a kind — 'default' or 'gift' — at call time.
  TEMPLATE_SELECTOR: '[data-cart-line-template="%kind%"]',
  MIN_QTY: 1,
  MAX_QTY: 99,
  // The only quantity a clone may stand for: the trigger publishes a unit price.
  CLONE_QTY: 1,
  // Stamped by cart-line-item.liquid. A gift's units may be excluded from the badge
  // the drawer renders, so the count prediction has to know which it is holding.
  IS_GIFT_ATTRIBUTE: 'data-line-is-gift',
  // Every server-computed money figure in the host, and the class cueing that one is
  // recomputing. Cued, never predicted: a cart-level discount does not scale linearly
  // with quantity, so no arithmetic here could be trusted to be right.
  AMOUNT_SELECTOR: '[data-cart-amount]',
  AMOUNT_STALE_CLASS: 'cart-amount-stale',

  // Per line key: the values Liquid last rendered, plus the count ledger.
  _settled: new Map(),

  // Per variant id: the add path's clone reference, plus the count ledger.
  _adds: new Map(),

  // Predictions land in `pending`; `dispatch` freezes them into one FIFO slice.
  // A resolution answers only for its own slice, never for a later prediction's.
  // `isGift` rides on the entry rather than being re-derived per call: a rollback
  // fires long after the row it belongs to may have stopped existing.
  _ledger(extra = {}) {
    return { pending: 0, slices: [], isGift: false, ...extra };
  },

  // Predicting and recording are one step so the badges and the ledger cannot diverge.
  _predict(entry, delta) {
    entry.pending += delta;
    window.CartStore?.predictCount(delta, { isGift: entry.isGift });
  },

  // Always pushes, even a zero slice: one dispatch must map to exactly one resolution.
  _dispatchLedger(entry) {
    if (!entry) return;
    entry.slices.push(entry.pending);
    entry.pending = 0;
  },

  // Oldest first — CartMutations._chain resolves in dispatch order.
  _takeSlice(entry) {
    return entry.slices.shift() ?? 0;
  },

  _isSpent(entry) {
    return entry.pending === 0 && entry.slices.length === 0;
  },

  // Capture before the first write for a key, never after — a second capture would
  // record an already-predicted value as if the server had sent it.
  _capture(row) {
    const { itemKey } = row.dataset;
    if (!itemKey) return null;

    const existing = this._settled.get(itemKey);
    if (existing) return existing;

    const entry = this._ledger({
      isGift: row.hasAttribute(this.IS_GIFT_ATTRIBUTE),
      quantity: row.querySelector('[data-cart-line-qty]')?.value ?? null,
      priceText: row.querySelector('[data-cart-line-price]')?.textContent ?? null,
      comparePriceText: row.querySelector('[data-cart-line-compare-price]')?.textContent ?? null,
    });
    this._settled.set(itemKey, entry);
    return entry;
  },

  // Paints the four line-local values in the same frame as the click. Nothing here
  // is styled as pending: the point of the prediction is that the row looks done.
  paintLine(row, quantity) {
    if (!row) return;

    const input = row.querySelector('[data-cart-line-qty]');
    if (!input) return;

    const entry = this._capture(row);
    const previousQty = Number.parseInt(input.value, 10);

    input.value = quantity;
    this._paintAmounts(row, quantity);
    this._paintControls(row, quantity);
    this._markAmountsStale(row.closest('[data-money-format]'));

    // The site header's badge sits outside every morph target, and the drawer's own
    // is server-rendered inside one, so both are predicted here per click rather
    // than once per request.
    if (entry && !Number.isNaN(previousQty)) this._predict(entry, quantity - previousQty);
  },

  // The only client arithmetic in the cart: quantity times a per-unit price that
  // Liquid rendered. Absent attributes mean the line is not predictable (a gift
  // line, or a line with no markdown to strike) and are skipped, not guessed at.
  _paintAmounts(row, quantity) {
    const priceEl = row.querySelector('[data-cart-line-price]');
    const unitPrice = Number.parseInt(row.dataset.lineUnitPrice ?? '', 10);
    if (priceEl && !Number.isNaN(unitPrice)) {
      const text = this._amountText(quantity * unitPrice, row.closest('[data-money-format]'));
      if (text) priceEl.textContent = text;
    }

    const compareEl = row.querySelector('[data-cart-line-compare-price]');
    const unitCompare = Number.parseInt(row.dataset.lineUnitComparePrice ?? '', 10);
    if (compareEl && !Number.isNaN(unitCompare) && unitCompare > 0) {
      const text = this._amountText(quantity * unitCompare, row.closest('[data-money-format]'));
      if (text) compareEl.textContent = text;
    }

    // The percent chip is left alone on purpose: both line totals scale linearly
    // with quantity, so its value is quantity-invariant and already correct.
  },

  // Without this the control keeps the minus icon and announces "decrease" all the
  // way down to quantity 1, when it has already become the remove control.
  _paintControls(row, quantity) {
    const minusBtn = row.querySelector('[data-quantity-minus]');
    if (minusBtn) {
      const isRemove = quantity === this.MIN_QTY;
      const { labelRemove, labelDecrease } = minusBtn.dataset;
      const label = isRemove ? labelRemove : labelDecrease;
      if (label) minusBtn.setAttribute('aria-label', label);

      const deleteIcon = minusBtn.querySelector('[data-icon-delete]');
      const minusIcon = minusBtn.querySelector('[data-icon-minus]');
      if (deleteIcon) deleteIcon.hidden = !isRemove;
      if (minusIcon) minusIcon.hidden = isRemove;
    }

    // aria-disabled, not disabled — matches what quantity-stepper.liquid renders, so
    // the button stays focusable and its bound max message stays reachable.
    const plusBtn = row.querySelector('[data-quantity-plus]');
    if (!plusBtn) return;

    if (quantity >= this.MAX_QTY) plusBtn.setAttribute('aria-disabled', 'true');
    else plusBtn.removeAttribute('aria-disabled');
  },

  // Mirrors snippets/price-display.liquid's filter chain and holds no currency or
  // separator literal of its own — the host root carries the spec, derived in Liquid
  // from the same filters. Private and off `window` by design: this must never grow
  // into a general money formatter competing with Shopify's own.
  _amountText(cents, formatRoot) {
    const spec = formatRoot?.dataset.moneyFormat ?? '';
    if (!this._canFormat(spec)) return '';

    const [moneyPrefix, moneyGroup, moneyDecimal] = spec.split('|');
    return this._formatCents(cents, moneyPrefix, moneyGroup, moneyDecimal);
  },

  // The probe amounts cart-money-format.liquid renders through price-display. Any
  // change here must change that snippet in the same commit.
  PROBE_FRACTIONAL: 123456,
  PROBE_WHOLE: 100000,

  // Verified specs, keyed by the raw attribute — the check runs once per format.
  _formatChecked: new Map(),

  // The shop's money format is only usable if this formatter reproduces Liquid's own
  // output byte for byte. That is what bounds the js-standards deviation: a currency
  // whose decimal count, grouping or symbol placement this cannot render falls back
  // to '' and the caller paints nothing, rather than painting a wrong amount.
  _canFormat(spec) {
    const cached = this._formatChecked.get(spec);
    if (cached !== undefined) return cached;

    const [moneyPrefix, moneyGroup, moneyDecimal, probeFractional, probeWhole] = spec.split('|');

    let ok = Boolean(moneyPrefix && moneyGroup && moneyDecimal && probeFractional && probeWhole);
    if (ok) {
      ok =
        this._formatCents(this.PROBE_FRACTIONAL, moneyPrefix, moneyGroup, moneyDecimal) === probeFractional &&
        this._formatCents(this.PROBE_WHOLE, moneyPrefix, moneyGroup, moneyDecimal) === probeWhole;
    }

    this._formatChecked.set(spec, ok);
    return ok;
  },

  // toFixed(2) fixes this at two-decimal currencies. A 0-decimal (JPY, KRW) or
  // 3-decimal (KWD, BHD) shop fails the probe above and paints nothing, by design.
  _formatCents(cents, moneyPrefix, moneyGroup, moneyDecimal) {
    const [whole, fraction] = (Math.max(0, Math.round(cents)) / 100).toFixed(2).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, moneyGroup);

    // Matches `remove: '.00'` — whole amounts render without a cents pair.
    if (fraction === '00') return `${moneyPrefix}${grouped}`;
    return `${moneyPrefix}${grouped}${moneyDecimal}${fraction}`;
  },

  // The row stays in the DOM until the server confirms, so there is no parent or
  // sibling to remember and no re-insertion to get wrong. The morph removes it.
  markLeaving(row) {
    if (!row) return;

    const entry = this._capture(row);
    const quantity = Number.parseInt(row.querySelector('[data-cart-line-qty]')?.value ?? '', 10);

    row.classList.add(this.LEAVING_CLASS);
    row.setAttribute('aria-busy', 'true');
    this._markAmountsStale(row.closest('[data-money-format]'));

    if (entry && !Number.isNaN(quantity)) this._predict(entry, -quantity);
  },

  // Marked at click time rather than on `cart:mutation-start`: a burst sits in the
  // coalescing window for up to CartQuantity.DEBOUNCE_MS before any request exists,
  // and a frozen total reads as broken for exactly that long. The host root carries
  // the money format, so it also bounds the amounts.
  _markAmountsStale(root) {
    if (!root) return;
    for (const el of root.querySelectorAll(this.AMOUNT_SELECTOR)) el.classList.add(this.AMOUNT_STALE_CLASS);
  },

  // The morph clears these by replacing them; this covers a failure, which never
  // morphs, and is called by the hosts once the mutation chain drains.
  clearAmountsStale(root) {
    if (!root) return;
    for (const el of root.querySelectorAll(this.AMOUNT_SELECTOR)) el.classList.remove(this.AMOUNT_STALE_CLASS);
  },

  // Called immediately before the request leaves, on the line paths.
  dispatch(key) {
    this._dispatchLedger(this._settled.get(key));
  },

  // Called immediately before the request leaves, on the add path.
  dispatchAdd(variantId) {
    this._dispatchLedger(this._adds.get(String(variantId)));
  },

  // Restores the line to the values Liquid last rendered into it. Callers gate this
  // on CartMutations.isLatest — when a newer mutation is queued its morph repaints
  // the line from truth, so an older failure must not paint over it.
  revert(key, root) {
    const entry = this._settled.get(key);

    const row = root?.querySelector(`[data-item-key="${CSS.escape(key)}"]`);
    if (row) {
      row.classList.remove(this.LEAVING_CLASS);
      row.removeAttribute('aria-busy');
    }

    if (!entry) return;

    const slice = this._takeSlice(entry);
    if (slice !== 0) window.CartStore?.predictCount(-slice, { isGift: entry.isGift });

    // A newer prediction still owns the row, so its values are not this one's to undo.
    if (!this._isSpent(entry)) return;

    this._settled.delete(key);
    if (!row) return;

    const input = row.querySelector('[data-cart-line-qty]');
    if (input && entry.quantity !== null) input.value = entry.quantity;

    const priceEl = row.querySelector('[data-cart-line-price]');
    if (priceEl && entry.priceText !== null) priceEl.textContent = entry.priceText;

    const compareEl = row.querySelector('[data-cart-line-compare-price]');
    if (compareEl && entry.comparePriceText !== null) compareEl.textContent = entry.comparePriceText;

    const quantity = Number.parseInt(entry.quantity ?? '', 10);
    if (!Number.isNaN(quantity)) this._paintControls(row, quantity);
  },

  // The morph has already rendered server truth over this slice, so it is spent.
  settle(key) {
    const entry = this._settled.get(key);
    if (!entry) return;

    this._takeSlice(entry);
    if (this._isSpent(entry)) {
      this._settled.delete(key);
      return;
    }

    // The entry outlives this mutation only to hold a newer slice's rollback; its
    // captured values are stale now, so a later revert must not restore them.
    entry.quantity = null;
    entry.priceText = null;
    entry.comparePriceText = null;
  },

  // Product content is fabricated only where the trigger makes it verifiably correct
  // at click time — title, thumbnail and unit price, at a quantity of one, where the
  // unit price is the line price. Everything else is omitted rather than guessed, and
  // a trigger failing the data test falls back to the skeleton, which claims nothing.
  markUnresolved(root, variantId, { quantity = 1, source = null } = {}) {
    if (!root || !variantId) return;

    const key = String(variantId);
    const entry =
      this._adds.get(key) ??
      this._ledger({
        row: null,
        confirmed: false,
        isGift: source instanceof Element && source.hasAttribute('data-add-is-gift'),
      });

    // Covers both "already in the cart" and "second rapid add of the same variant",
    // so a variant never gets a shimmer and a clone at once.
    const existing = root.querySelector(`[data-cart-items] [data-line-variant-id="${CSS.escape(key)}"]`);
    if (existing) {
      existing.classList.add(this.UNRESOLVED_CLASS);
      // An earlier add's clone: rollback must still be able to remove it.
      if (existing.hasAttribute('data-cart-line-optimistic')) entry.row = existing;
    } else {
      const clone = quantity === this.CLONE_QTY ? this._cloneRow(root, key, source) : null;
      if (clone) entry.row = clone;
      else {
        const skeleton = root.querySelector('[data-cart-skeleton]');
        if (skeleton) skeleton.hidden = false;
      }
    }

    // Every outcome predicts: the quantity and the variant are known in all three.
    this._adds.set(key, entry);
    this._predict(entry, quantity);
    this._markAmountsStale(root);
  },

  // Clones the Liquid-rendered template and fills it from the trigger's own data
  // attributes. Every write is textContent, src or hidden — no HTML string is built
  // and no amount is formatted; the price crosses as a Liquid-formatted string.
  _cloneRow(root, key, source) {
    if (!(source instanceof Element)) return null;

    const { productTitle, productPrice, productFeaturedImage } = source.dataset;
    const isGift = source.hasAttribute('data-add-is-gift');

    // A gift has no payable price to show, so its title alone is enough.
    if (!(isGift ? productTitle : productTitle || productPrice)) return null;

    const selector = this.TEMPLATE_SELECTOR.replace('%kind%', isGift ? 'gift' : 'default');
    const template = root.querySelector(selector);
    const row = template?.content.firstElementChild?.cloneNode(true);
    if (!row) return null;

    // The only key the clone carries — it lets a second add of the same variant find
    // this row instead of making another. It deliberately has no data-item-key.
    row.dataset.lineVariantId = key;

    const titleEl = row.querySelector('[data-optimistic-title]');
    if (titleEl) {
      if (productTitle) titleEl.textContent = productTitle;
      else titleEl.hidden = true;
    }

    const priceEl = row.querySelector('[data-optimistic-price]');
    if (priceEl) {
      if (productPrice) priceEl.textContent = productPrice;
      else priceEl.hidden = true;
    }

    // Left alone when there is no URL: the template ships with the neutral block
    // visible and the image src-less, so a missing image is never a broken <img>.
    const thumb = row.querySelector('[data-optimistic-thumb]');
    const thumbFallback = row.querySelector('[data-optimistic-thumb-fallback]');
    if (productFeaturedImage && thumb) {
      thumb.src = productFeaturedImage;
      thumb.hidden = false;
      if (thumbFallback) thumbFallback.hidden = true;
    }

    row.classList.add(this.UNRESOLVED_CLASS);

    // The line list when the cart has lines, so the clone inherits its gap; the items
    // shell when it is empty, because the region is not rendered at all.
    const host = root.querySelector('[data-cart-line-list]') ?? root.querySelector('[data-cart-items]');
    if (!host) return null;

    // Prepended — the Ajax cart puts a new line first, so the real row lands here.
    host.prepend(row);
    return row;
  },

  // Only the failure path needs this. Callers gate it on CartMutations.isLatest — a
  // superseded add must not remove a row a newer mutation has since rendered.
  clearUnresolved(root, variantId) {
    if (!root || !variantId) return;

    const key = String(variantId);
    const entry = this._adds.get(key);
    if (!entry) return;

    const slice = this._takeSlice(entry);
    if (slice !== 0) window.CartStore?.predictCount(-slice, { isGift: entry.isGift });

    // A newer add still owns the row and the skeleton, so neither is this one's to clear.
    if (!this._isSpent(entry)) return;

    this._adds.delete(key);

    // An earlier add of this variant succeeded, so the clone stands for a real line
    // now and must survive until the morph replaces it.
    if (!entry.confirmed) entry.row?.remove();

    const existing = root.querySelector(`[data-cart-items] [data-line-variant-id="${CSS.escape(key)}"]`);
    existing?.classList.remove(this.UNRESOLVED_CLASS);

    // Another in-flight add may still be relying on it.
    if (this._adds.size > 0) return;

    const skeleton = root.querySelector('[data-cart-skeleton]');
    if (skeleton) skeleton.hidden = true;
  },

  // The success path's mirror of settle. The morph, not this method, removes the
  // clone and re-hides the skeleton.
  settleAdd(variantId) {
    const key = String(variantId);
    const entry = this._adds.get(key);
    if (!entry) return;

    this._takeSlice(entry);
    entry.confirmed = true;

    if (this._isSpent(entry)) this._adds.delete(key);
    // The row reference is spent either way: this add justified whatever is on screen.
    else entry.row = null;
  },

  // Called when a host disconnects, which in the theme editor means a section
  // re-render: the new markup carries no predictions, so held entries are stale keys.
  reset() {
    this._settled.clear();
    this._adds.clear();
  },
};

window.CartOptimistic = CartOptimistic;
